// The MapLibre filter and ramp expressions the city layers are built from.
//
// These decide which dots and names are drawn at a given zoom, and how big.
// They live together because they are read from two directions — the layer
// builders use them at creation, and applyVariable re-applies them on every
// variable switch — so a copy in either place would drift.
//
// `highlightId` is the open city's id and rides along with them: it is what the
// filters close over, which is why it belongs here rather than in app state.

import type { ExpressionSpecification } from 'maplibre-gl';

// The markers are cities, not weather stations: a station is an instrument site
// (often an airport in the middle of nowhere), a city is a place you recognise.
// Each city's weather is interpolated from the surrounding station network,
// sampled server-side off the same grids the field is cut from.
//
// Cities are fetched per viewport, and which ones come back depends on zoom — the
// server sends only those big enough to draw at that scale (~133 at a world view,
// not all 42k; listed-only mz -1 cities never come back at all unless selected).
// Every city also carries `mz`, the zoom it starts appearing at, so the client
// filters locally too: a zoom change reveals or hides a tier instantly instead of
// waiting for the refetch to land.
export const visibleAtZoom: ExpressionSpecification = ['<=', ['get', 'mz'], ['zoom']];

// Names are held back one zoom behind the dots that carry them.
//
// A dot every 42px is a reading of the weather; a *name* every 42px is a wall of text,
// and the two are not the same density. The dot is the data and can be dense — you take
// it in at a glance, by colour. The name has to be read one at a time, and at zoom 5 in
// Europe there were 231 of them, which is not a map so much as a list. Waiting a zoom
// roughly quarters them and leaves the ones that earned it, while every dot keeps its
// tooltip either way.
export const namedAtZoom: ExpressionSpecification = ['<=', ['+', ['get', 'mz'], 1], ['zoom']];

// The open city is the one exception to both gates above. Its green ring
// (city-selected-ring) is drawn at every zoom, and citiesIn sends its marker at
// every zoom, so on its own /{country}/{city}/ page its dot and name must show
// at every zoom too — a small town otherwise reads as a bare ringed circle with
// no label until you zoom past its mz. `highlightId` is the open city's id (-1 =
// none, matches no feature), kept in step by setSelectedHighlight.
//
// The dot is exempted here (still gated on `has` a reading, for its colour); the
// NAME is handled by its own always-placed layer, `city-selected-label`, rather
// than by exempting it in the shared labels: a shared exemption still lost the
// name to zoom-behind collision with a bigger neighbour. The dedicated layer
// carries text-allow-overlap, so it is never gated or collided out, and the
// shared layer excludes the open city so the name never draws twice.
let highlightId = -1;

// Set by setSelectedHighlight when a city opens or closes. -1 matches no feature.
export function setHighlightId(id: number | null): void {
  highlightId = id == null ? -1 : id;
}
export const isOpenCity = (): ExpressionSpecification => ['==', ['get', 'id'], highlightId];
export const dotFilter = (v: string): ExpressionSpecification => ['all', ['has', v], ['any', visibleAtZoom, isOpenCity()]];
export const labelFilter = (v: string): ExpressionSpecification => ['all', ['has', v], namedAtZoom, ['!', isOpenCity()]];

// Population spans 27k to 38M, so anything size-related ramps on log10(pop) —
// on a linear ramp every city under a million would collapse to the same size.
const POP_LOG_MIN = Math.log10(27134); // smallest city drawn (Jonava, Lithuania)
const POP_LOG_MAX = Math.log10(38e6); // largest (Tokyo)
export const byPop = (small: number, big: number): ExpressionSpecification => [
  'interpolate',
  ['linear'],
  ['log10', ['get', 'pop']],
  POP_LOG_MIN,
  small,
  POP_LOG_MAX,
  big,
];
